import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Zap, Shield, DollarSign, MapPin, Clock, Users, Mail, Phone, MessageCircle, Check, ChevronRight, Truck, Car, Wrench, CircleDot, CarTaxiFront, Package } from "lucide-react";

function PageShell({ eyebrow, title, sub, children }) {
  return (
    <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-16">
      <div className="text-[11px] uppercase font-black tracking-widest text-brand-accent">{eyebrow}</div>
      <h1 className="text-4xl md:text-6xl font-black leading-tight mt-2">{title}</h1>
      {sub && <p className="mt-4 text-lg text-ink-textDim max-w-2xl">{sub}</p>}
      <div className="mt-10">{children}</div>
    </div>
  );
}

export function About() {
  const { t } = useTranslation();
  return (
    <PageShell eyebrow={t("about.eyebrow")} title={t("about.title")} sub={t("about.sub")}>
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { icon: Zap, t: t("about.f1t"), d: t("about.f1d") },
          { icon: MapPin, t: t("about.f2t"), d: t("about.f2d") },
          { icon: Shield, t: t("about.f3t"), d: t("about.f3d") },
        ].map((f, i) => (
          <div key={i} className="bg-ink-surface border border-ink-border rounded-2xl p-6">
            <div className="w-11 h-11 rounded-2xl bg-white text-black flex items-center justify-center"><f.icon size={20} /></div>
            <div className="text-lg font-black mt-4">{f.t}</div>
            <div className="text-sm text-ink-textDim mt-2 leading-relaxed">{f.d}</div>
          </div>
        ))}
      </div>

      <div className="mt-12 grid md:grid-cols-2 gap-8 items-start">
        <div>
          <div className="text-[10px] uppercase font-black tracking-widest text-brand-accent">{t("about.storyEyebrow")}</div>
          <h2 className="text-3xl font-black mt-2">{t("about.storyTitle")}</h2>
          <p className="mt-4 text-ink-textDim leading-relaxed">{t("about.storyP1")}</p>
          <p className="mt-3 text-ink-textDim leading-relaxed">{t("about.storyP2")}</p>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[Truck, Car, Wrench, CircleDot, CarTaxiFront, Package].map((Icon, i) => (
            <div key={i} className="aspect-square bg-ink-surface border border-ink-border rounded-2xl flex items-center justify-center">
              <Icon size={28} strokeWidth={2} />
            </div>
          ))}
        </div>
      </div>
    </PageShell>
  );
}

export function Pricing() {
  const { t } = useTranslation();
  const plans = [
    { key: "customer", price: t("pricing.free"), features: ["c1", "c2", "c3", "c4"], cta: t("pricing.ctaSignUp"), href: "/register" },
    { key: "supplier", price: "10%", priceSub: t("pricing.perJob"), features: ["s1", "s2", "s3", "s4"], cta: t("pricing.ctaBecomeSupplier"), href: "/register?role=supplier", highlight: true },
  ];
  return (
    <PageShell eyebrow={t("pricing.eyebrow")} title={t("pricing.title")} sub={t("pricing.sub")}>
      <div className="grid md:grid-cols-2 gap-4 max-w-4xl">
        {plans.map((p) => (
          <div key={p.key} className={`rounded-3xl p-8 border-2 ${p.highlight ? "border-white bg-white/5" : "border-ink-border bg-ink-surface"}`}>
            <div className="text-[10px] uppercase font-black tracking-widest text-brand-accent">{t(`pricing.${p.key}Eyebrow`)}</div>
            <div className="text-2xl font-black mt-1">{t(`pricing.${p.key}Title`)}</div>
            <div className="mt-6 flex items-end gap-2">
              <div className="text-5xl font-black">{p.price}</div>
              {p.priceSub && <div className="text-xs text-ink-textDim pb-2 font-bold">{p.priceSub}</div>}
            </div>
            <div className="mt-6 space-y-3">
              {p.features.map((f) => (
                <div key={f} className="flex items-start gap-2 text-sm">
                  <div className="w-5 h-5 rounded-full bg-brand-accent/20 text-brand-accent flex items-center justify-center flex-shrink-0 mt-0.5"><Check size={12} strokeWidth={3} /></div>
                  <span>{t(`pricing.${p.key}${f.slice(-1)}`)}</span>
                </div>
              ))}
            </div>
            <Link to={p.href} className={`mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 font-black text-sm ${p.highlight ? "bg-white text-black" : "bg-ink-surface2 border border-ink-border text-white"}`}>
              {p.cta} <ChevronRight size={16} className="rtl-flip" />
            </Link>
          </div>
        ))}
      </div>
      <div className="mt-10 text-sm text-ink-textDim max-w-2xl">{t("pricing.footnote")}</div>
    </PageShell>
  );
}

export function Contact() {
  const { t } = useTranslation();
  return (
    <PageShell eyebrow={t("contact.eyebrow")} title={t("contact.title")} sub={t("contact.sub")}>
      <div className="grid md:grid-cols-3 gap-4 max-w-4xl">
        {[
          { icon: Mail, label: t("contact.email"), value: "support@drivon.com", href: "mailto:support@drivon.com" },
          { icon: Phone, label: t("contact.phone"), value: "+1 (555) 010-9911", href: "tel:+15550109911" },
          { icon: MessageCircle, label: t("contact.chat"), value: t("contact.chatValue"), href: "/services" },
        ].map((c) => (
          <a key={c.label} href={c.href} className="bg-ink-surface border border-ink-border rounded-2xl p-6 hover:border-white transition-colors">
            <div className="w-11 h-11 rounded-2xl bg-white text-black flex items-center justify-center"><c.icon size={20} /></div>
            <div className="text-[10px] uppercase font-black tracking-widest text-ink-textDim mt-4">{c.label}</div>
            <div className="text-base font-black mt-1">{c.value}</div>
          </a>
        ))}
      </div>

      <div className="mt-10 bg-ink-surface border border-ink-border rounded-3xl p-8 max-w-4xl">
        <div className="grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="text-[10px] uppercase font-black tracking-widest text-brand-accent">{t("contact.hoursEyebrow")}</div>
            <div className="text-2xl font-black mt-1">{t("contact.hoursTitle")}</div>
            <p className="text-sm text-ink-textDim mt-3">{t("contact.hoursDesc")}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-full bg-brand-accent/20 text-brand-accent flex items-center justify-center"><Clock size={26} /></div>
            <div>
              <div className="text-4xl font-black">24 / 7</div>
              <div className="text-xs text-ink-textDim font-bold uppercase">{t("contact.dispatchLabel")}</div>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
